import * as React from "react"

import { cn } from "../../cn"
import { Badge } from "../Badge/Badge"
import { TruncatedText } from "../TruncatedText/TruncatedText"

export interface SelectionChipProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "children"
> {
  /** What was selected. Truncates by default rather than growing the chip. */
  label: React.ReactNode
  /**
   * Allow sentence-length labels to wrap instead of truncating. The chip grows
   * past the control height to fit them.
   */
  wrapLabel?: boolean
  /** Leading content, e.g. a `ColorSwatch` carrying a tag's own color. */
  leading?: React.ReactNode
  /** Omit for a chip the user cannot remove. */
  onRemove?: () => void
  /**
   * Accessible name for the remove button. Defaults to `Remove <label>` when
   * the label is a string.
   */
  removeLabel?: string
  /**
   * A selection whose record could not be resolved — a deleted id, say. Reads
   * as an error rather than as a live selection.
   */
  invalid?: boolean
}

/**
 * The chip for anything the user has selected: a filter applied to a table, an
 * entity picked in a launcher, a value added to a list.
 *
 * One appearance across the product, so a selection reads the same wherever it
 * lands, and one height, `--height-control`, so a row of chips lines up with
 * the buttons and dropdowns beside it. Reach for `Badge` instead when the chip
 * reports state the user did not choose — a run's status, a metric's verdict.
 *
 * A label too long for the chip is clipped and comes back whole in a tooltip
 * on hover, so hand the chip the full text rather than shortening it first.
 */
const SelectionChip = React.forwardRef<HTMLSpanElement, SelectionChipProps>(
  (
    {
      label,
      wrapLabel = false,
      leading,
      onRemove,
      removeLabel,
      invalid = false,
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <Badge
        ref={ref}
        variant={invalid ? "danger" : "info"}
        size="chip"
        // Chips hold their width and let the row wrap or scroll rather than
        // squeezing every label into an ellipsis.
        className={cn(
          "max-w-56 shrink-0",
          // A wrapping label needs room to grow past the control height, and
          // its remove button sits with the first line rather than centred
          // against the whole block.
          wrapLabel && "h-auto min-h-[var(--height-control)] items-start py-1",
          className,
        )}
        onDismiss={onRemove}
        dismissLabel={
          removeLabel ??
          (typeof label === "string" ? `Remove ${label}` : "Remove")
        }
        {...props}
      >
        {leading}
        {wrapLabel ? (
          <span className="whitespace-normal break-words">{label}</span>
        ) : (
          <TruncatedText>{label}</TruncatedText>
        )}
      </Badge>
    )
  },
)

SelectionChip.displayName = "SelectionChip"

export { SelectionChip }
